import React, { useEffect, useState } from "react";
import { CheckCircle, AlertTriangle, RefreshCw, MessageSquareText, GraduationCap } from "lucide-react";
import TenantPreferencesService, {
  AtlasConfigResult,
  AtlasEffectiveConfig,
} from "../../_services/TenantPreferencesService";

interface Props {
  onError: (msg: string | null) => void;
  onSuccess: (msg: string | null) => void;
}

const SAMPLE_OTP = "483920";

/**
 * Atlas tab — registration wiring (default program + partner school) and
 * branding (brand name + OTP SMS template) for the Atlas career-guidance
 * sub-app. Persists to the S3 app-properties.json `atlas` block via
 * PUT /v1/tenant-config/atlas; the landing copy in the same block is left
 * untouched (edit via iLearn App Properties → Raw JSON if needed).
 *
 * This screen is the resolution path for error codes ATL-1001 / ATL-1002 —
 * the status banner at the top mirrors exactly what the register endpoint
 * validates, and the dropdowns only offer ids that exist for this tenant.
 */
const AtlasTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [effective, setEffective] = useState<AtlasEffectiveConfig | null>(null);
  const [programs, setPrograms] = useState<{ id: number; name: string }[]>([]);
  const [schools, setSchools] = useState<{ id: number; name: string }[]>([]);

  // Draft fields (tenant-level values only; "" = no tenant override)
  const [brandName, setBrandName] = useState("");
  const [smsTemplate, setSmsTemplate] = useState("");
  const [programId, setProgramId] = useState<string>("");
  const [partnerId, setPartnerId] = useState<string>("");
  const [original, setOriginal] = useState<string>("");

  const snapshot = (b: string, t: string, p: string, s: string) => JSON.stringify([b, t, p, s]);

  const applyResult = (data: AtlasConfigResult) => {
    const atlas = data.atlas || {};
    const b = typeof atlas.brandName === "string" ? atlas.brandName : "";
    const t = typeof atlas.smsTemplate === "string" ? atlas.smsTemplate : "";
    const p = atlas.defaultProgramId ? String(atlas.defaultProgramId) : "";
    const s = atlas.partnerId ? String(atlas.partnerId) : "";
    setBrandName(b);
    setSmsTemplate(t);
    setProgramId(p);
    setPartnerId(s);
    setOriginal(snapshot(b, t, p, s));
    setEffective(data.effective);
    setPrograms(data.programs);
    setSchools(data.schools);
  };

  const load = async () => {
    setLoading(true);
    try {
      applyResult(await TenantPreferencesService.getAtlasConfig());
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to load Atlas config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = snapshot(brandName, smsTemplate, programId, partnerId) !== original;

  const save = async () => {
    onError(null);
    if (smsTemplate.trim() && !smsTemplate.includes("{otp}")) {
      return onError("The SMS template must contain the {otp} placeholder.");
    }
    setSaving(true);
    try {
      await TenantPreferencesService.saveAtlasConfig({
        brandName,
        smsTemplate,
        defaultProgramId: programId === "" ? null : Number(programId),
        partnerId: partnerId === "" ? null : Number(partnerId),
      });
      // Re-fetch so the effective/validity view reflects what the server resolves now.
      applyResult(await TenantPreferencesService.getAtlasConfig());
      onSuccess(
        "Atlas settings saved. New registrations pick this up within 5 minutes; the student app reads it on the next page load.",
      );
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to save Atlas settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tp-loading">Loading Atlas settings…</div>;

  const previewBrand = brandName.trim() || effective?.brandName || "Atlas";
  const previewTemplate =
    smsTemplate.trim() || effective?.smsTemplate || "Your {brandName} verification code is {otp}.";
  const smsPreview = previewTemplate
    .replace(/\{brandName\}/g, previewBrand)
    .replace(/\{otp\}/g, SAMPLE_OTP);

  const registrationBroken = effective && !effective.defaultProgramValid;
  const partnerBroken = effective && !effective.partnerValid;

  const sourceHint = (source: "tenant" | "platform" | null) =>
    source === "tenant"
      ? "set for this tenant"
      : source === "platform"
        ? "inherited platform default — set a tenant value to be safe across upgrades"
        : "not set";

  return (
    <div className="tp-atlas">
      {/* ---- Registration health ---- */}
      <div className={`tp-alert ${registrationBroken ? "tp-alert-error" : "tp-alert-success"}`}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {registrationBroken ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {registrationBroken
            ? "Student registration is currently BROKEN for Atlas (error code ATL-1001): the effective default program does not exist for this tenant. Pick a program below and save."
            : partnerBroken
              ? "Registration works, but the partner school is not valid for this tenant (error code ATL-1002) — partner reporting will be blank. Pick a partner below."
              : "Atlas registration is correctly configured for this tenant."}
        </span>
      </div>

      {/* ---- Registration wiring ---- */}
      <div className="tp-card">
        <h3>
          <GraduationCap size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
          Registration
        </h3>
        <p className="tp-card-desc">
          Every new Atlas registration is filed as an enquiry under a program and attributed to a
          partner school. Both dropdowns only offer entries that exist for this institution, so a
          value picked here can never break registration.
        </p>
        <div className="tp-dimensions" style={{ maxWidth: 720 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-atlas-program">Default program for new registrations</label>
            <select
              id="tp-atlas-program"
              className="tp-field-input"
              value={programId}
              onChange={(e) => setProgramId(e.target.value)}
            >
              <option value="">(use platform default{effective?.defaultProgramId ? ` — currently ${effective.defaultProgramId}` : ""})</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (#{p.id})
                </option>
              ))}
            </select>
            <div className="tp-slot-hint">
              Effective now: {effective?.defaultProgramId ?? "—"} ({sourceHint(effective?.defaultProgramSource ?? null)}).
              Tip: create a dedicated program named after your Atlas brand to keep these enquiries separate.
            </div>
          </div>
          <div>
            <label className="tp-field-label" htmlFor="tp-atlas-partner">Partner school</label>
            <select
              id="tp-atlas-partner"
              className="tp-field-input"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              <option value="">(use platform default{effective?.partnerId ? ` — currently ${effective.partnerId}` : ""})</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} (#{s.id})
                </option>
              ))}
            </select>
            <div className="tp-slot-hint">
              Effective now: {effective?.partnerId ?? "—"} ({sourceHint(effective?.partnerSource ?? null)}).
            </div>
          </div>
        </div>
      </div>

      {/* ---- Branding ---- */}
      <div className="tp-card">
        <h3>
          <MessageSquareText size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
          Brand &amp; OTP SMS
        </h3>
        <p className="tp-card-desc">
          The brand name appears in the OTP SMS (and is available to the student app). The
          template must contain <code>{"{otp}"}</code>; <code>{"{brandName}"}</code> is replaced
          with the brand name.
        </p>
        <div className="tp-dimensions" style={{ maxWidth: 720 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-atlas-brand">Brand name</label>
            <input
              id="tp-atlas-brand"
              className="tp-field-input"
              type="text"
              maxLength={60}
              placeholder={`e.g. "SelectEd U" — blank uses the default "Atlas"`}
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
            />
          </div>
          <div>
            <label className="tp-field-label" htmlFor="tp-atlas-sms">OTP SMS template</label>
            <textarea
              id="tp-atlas-sms"
              className="tp-field-input"
              rows={3}
              maxLength={300}
              placeholder="Your {brandName} verification code is {otp}."
              value={smsTemplate}
              onChange={(e) => setSmsTemplate(e.target.value)}
              spellCheck={false}
            />
            <div className="tp-slot-hint">
              Preview: <em>{smsPreview}</em>
            </div>
          </div>
        </div>
      </div>

      <div className="tp-toolbar">
        {dirty && <span className="tp-dirty-flag">Unsaved changes</span>}
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={() => void load()}
          disabled={saving}
        >
          <RefreshCw size={14} /> Reload
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={() => void save()}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default AtlasTab;
